import { useEffect, useState } from 'react';
import {
  ApiError,
  api,
  engineLabel,
  kindLabel,
  type OrgAdoption,
  type OrgMember,
  type OrgNode,
  type OrgPerson,
  type OrgSchedule,
  type OrgSummary,
  type OrgUnit,
} from '../lib/api';
import { navigate } from '../lib/router';

/**
 * The customer tree, as a screen (D38).
 *
 * This is the page that answers James's question — "will it be possible to
 * add Departments, Divisions, sub-divisions and multiple schedules at a later
 * point?" — by handing it over. Every level of a client's organization is
 * added here, by the people who run it, without anybody deploying anything.
 *
 * It is one screen rather than a page per level on purpose: a hospital, a
 * department, a division and a group are the same thing to this platform (a
 * node with a parent), so they get the same screen. A level nobody has named
 * yet will get it too.
 */
export function Organization({ clientId, unitId, owner }: { clientId: string | null; unitId: string | null; owner: boolean }) {
  const [clients, setClients] = useState<OrgUnit[] | null>(null);
  const [tree, setTree] = useState<OrgNode | null>(null);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [error, setError] = useState<string | null>(null);

  // An owner picks a client; everybody else has exactly one and never sees a
  // picker. Either way the id in the URL is what the page reads.
  const loadClients = (select?: string) => {
    setError(null);
    api
      .orgClients()
      .then((rows) => {
        setClients(rows);
        const want = select ?? clientId;
        if (!want && rows[0]) go(rows[0].id, null);
        else if (select) go(select, null);
      })
      .catch((e: Error) => setError(e.message));
  };
  useEffect(() => loadClients(), []);

  const loadTree = () => {
    if (!clientId) return;
    setError(null);
    Promise.all([api.orgTree(clientId), api.orgMemberCounts(clientId)])
      .then(([t, c]) => { setTree(t); setCounts(c); })
      .catch((e: Error) => setError(e.message));
  };
  useEffect(loadTree, [clientId]);

  if (error) return <p className="error">Couldn't load the organization ({error}).</p>;
  if (!clients) return <p className="muted">Loading…</p>;
  // Nothing set up yet — the state a freshly deployed service is in, since
  // migrations create the tables but nothing fills them. An owner sets the
  // first client up here; everybody else is told who does.
  if (clients.length === 0) {
    return owner ? (
      <FirstRun onDone={(id) => loadClients(id)} />
    ) : (
      <p className="muted">No organization has been set up here yet — CallCovered does that first.</p>
    );
  }
  if (!tree) return <p className="muted">Loading…</p>;

  const flat = flatten(tree);
  const current = (unitId && flat.find((u) => u.id === unitId)) || tree;
  const trail = breadcrumb(tree, current.id);

  return (
    <section className="org">
      <div className="gridtools">
        {owner && (
          <label className="field inline">
            <span className="muted">Client</span>
            <select value={clientId ?? ''} onChange={(e) => go(e.target.value, null)}>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>
        )}
        {owner && <AddClient onAdded={(id) => loadClients(id)} />}
        <nav className="crumbs" aria-label="Where you are">
          {trail.map((node, i) => (
            <span key={node.id}>
              {i > 0 && <span className="muted"> › </span>}
              {node.id === current.id ? (
                <strong>{node.name}</strong>
              ) : (
                <button type="button" className="linklike" onClick={() => go(clientId!, node.id)}>{node.name}</button>
              )}
            </span>
          ))}
        </nav>
      </div>

      <p className="legend muted">
        Everything here is yours to change. A department, a division, a level below a division — each is added on this
        page and takes effect straight away. Hospitals keep their own copy of a department, so two hospitals can both
        have Internal Medicine without sharing a single person or schedule.
      </p>

      <div className="logi-cols">
        <div className="logi-main">
          <Children node={current} counts={counts} clientId={clientId!} onGo={(id) => go(clientId!, id)} onChanged={loadTree} />
          <AddUnit clientId={clientId!} parent={current} onAdded={loadTree} />
        </div>
        <div className="logi-side">
          <UnitCard key={current.id} clientId={clientId!} unit={current} units={flat} onChanged={loadTree} onGo={(id) => go(clientId!, id)} />
          <Schedules clientId={clientId!} unit={current} units={flat} />
          <People clientId={clientId!} unit={current} onChanged={loadTree} />
        </div>
      </div>
    </section>
  );
}

const go = (clientId: string, unitId: string | null) =>
  navigate(`/admin/org?clientId=${clientId}${unitId ? `&unitId=${unitId}` : ''}`);

/**
 * The screen a freshly deployed service shows: the tables exist and nothing
 * is in them. Migrations run themselves on this platform but no seed does,
 * and the database has no public address — so this form, not a database
 * tunnel, is how the first client comes into being.
 *
 * "Adopt what is already here" is for the client that predates the tree. It
 * takes the existing hospitals and roster into that client's branch without
 * changing how any of it runs, and is safe to repeat.
 */
function FirstRun({ onDone }: { onDone: (clientId: string) => void }) {
  const [name, setName] = useState('');
  const [adopt, setAdopt] = useState(true);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [done, setDone] = useState<OrgAdoption | null>(null);

  const run = async () => {
    setBusy(true);
    setProblem(null);
    try {
      const res = await api.orgSetUp({ name: name.trim(), adoptExisting: adopt });
      setDone(res.adopted);
      onDone(res.client.id);
    } catch (e) {
      setProblem(e instanceof ApiError ? e.message : (e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="org">
      <div className="card logi">
        <h2>Set up your organization</h2>
        <p className="muted">
          Nothing is set up here yet. Name the organization this service belongs to and it becomes the top of its own
          tree — hospitals, departments and divisions all hang beneath it, and you add those yourself from the next
          screen.
        </p>
        <label className="field">
          <span>Organization name <span className="muted">— as people say it</span></span>
          <input value={name} maxLength={120} onChange={(e) => setName(e.target.value)} placeholder="e.g. Open Source MD" />
        </label>
        <label className="field check">
          <input type="checkbox" checked={adopt} onChange={(e) => setAdopt(e.target.checked)} />
          <span>
            Adopt what is already here <span className="muted">— brings the hospitals and people this service already
            schedules into the tree. Nothing about how they run changes.</span>
          </span>
        </label>
        {problem && <p className="error">{problem}</p>}
        {done && (
          <p className="ok small">
            Adopted {done.hospitals} hospital{done.hospitals === 1 ? '' : 's'}, {done.schedules} schedule
            {done.schedules === 1 ? '' : 's'}, {done.weeksLinked} stored week{done.weeksLinked === 1 ? '' : 's'} and{' '}
            {done.placed} {done.placed === 1 ? 'person' : 'people'}.
          </p>
        )}
        <div className="formactions">
          <button type="button" className="primary" disabled={busy || !name.trim()} onClick={run}>
            {busy ? 'Setting up…' : 'Set up'}
          </button>
        </div>
        <p className="legend muted">
          Safe to run more than once — nothing is duplicated, so an interrupted run is finished by repeating it.
        </p>
      </div>
    </section>
  );
}

/** Take on another client. Only CallCovered ever sees this. */
function AddClient({ onAdded }: { onAdded: (clientId: string) => void }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const add = async () => {
    setBusy(true);
    setProblem(null);
    try {
      const res = await api.orgSetUp({ name: name.trim(), adoptExisting: false });
      setName('');
      setOpen(false);
      onAdded(res.client.id);
    } catch (e) {
      setProblem(e instanceof ApiError ? e.message : (e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button type="button" className="linklike small" onClick={() => setOpen(true)}>
        Add a client
      </button>
    );
  }
  return (
    <div className="addclient">
      <input
        value={name}
        maxLength={120}
        onChange={(e) => setName(e.target.value)}
        placeholder="e.g. NYU Langone Health"
        onKeyDown={(e) => { if (e.key === 'Enter' && name.trim()) add(); }}
      />
      <button type="button" className="primary" disabled={busy || !name.trim()} onClick={add}>
        {busy ? 'Adding…' : 'Add'}
      </button>
      <button type="button" onClick={() => { setOpen(false); setProblem(null); }}>Cancel</button>
      {problem && <p className="error small">{problem}</p>}
    </div>
  );
}

/** What sits directly under here, each with everybody beneath it counted. */
function Children({
  node,
  counts,
  clientId,
  onGo,
  onChanged,
}: {
  node: OrgNode;
  counts: Record<string, number>;
  clientId: string;
  onGo: (id: string) => void;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  const setActive = async (unit: OrgNode, active: boolean) => {
    setBusy(unit.id);
    setProblem(null);
    try {
      await api.orgUpdateUnit(clientId, unit.id, { active });
      onChanged();
    } catch (e) {
      setProblem(e instanceof ApiError ? e.message : (e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="card logi">
      <h2>Inside {node.name}</h2>
      {problem && <p className="error">{problem}</p>}
      {node.children.length === 0 ? (
        <p className="muted">
          Nothing inside {node.name} yet. Add the first {nextKindLabel(node.kind).toLowerCase()} below.
        </p>
      ) : (
        <ul className="orgchildren">
          {node.children.map((c) => (
            <li key={c.id} className={c.active ? undefined : 'inactive'}>
              <button type="button" className="linklike orgname" onClick={() => onGo(c.id)}>{c.name}</button>
              <span className="pill tiny">{kindLabel(c.kind)}</span>
              {c.children.length > 0 && <span className="muted small">{c.children.length} inside</span>}
              {counts[c.id] ? <span className="muted small">{counts[c.id]} {counts[c.id] === 1 ? 'person' : 'people'}</span> : null}
              {!c.active && <span className="pill tiny warn">closed</span>}
              <span className="orgactions">
                <button type="button" disabled={busy === c.id} onClick={() => setActive(c, !c.active)}>
                  {c.active ? 'Close' : 'Reopen'}
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}
      <p className="legend muted">
        Closing keeps a unit and its history readable but takes it out of everyday use. Nothing here is ever deleted.
      </p>
    </div>
  );
}

/** Add a level. The kind is a plain list plus anything you type, which is the whole point of the design. */
function AddUnit({ clientId, parent, onAdded }: { clientId: string; parent: OrgNode; onAdded: () => void }) {
  const suggested = nextKind(parent.kind);
  const [kind, setKind] = useState(suggested);
  const [name, setName] = useState('');
  const [templateKey, setTemplateKey] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  // Moving to another unit changes what you are most likely adding next.
  useEffect(() => { setKind(nextKind(parent.kind)); setName(''); setTemplateKey(''); setProblem(null); }, [parent.id]);

  const add = async () => {
    setBusy(true);
    setProblem(null);
    try {
      await api.orgCreateUnit(clientId, {
        parentId: parent.id,
        kind: kind.trim(),
        name: name.trim(),
        templateKey: templateKey.trim() || null,
      });
      setName('');
      setTemplateKey('');
      onAdded();
    } catch (e) {
      setProblem(e instanceof ApiError ? e.message : (e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card logi">
      <h2>Add something inside {parent.name}</h2>
      <label className="field">
        <span>What kind</span>
        <input
          list="org-kinds"
          value={kind}
          maxLength={40}
          onChange={(e) => setKind(e.target.value)}
          placeholder="e.g. division"
        />
        <datalist id="org-kinds">
          {KINDS.map((k) => <option key={k} value={k}>{kindLabel(k)}</option>)}
        </datalist>
      </label>
      <label className="field">
        <span>Name <span className="muted">— as people say it</span></span>
        <input
          value={name}
          maxLength={120}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Department of Internal Medicine"
        />
      </label>
      <label className="field">
        <span>
          Same-kind tag <span className="muted">— optional; ties this to the matching unit at other hospitals</span>
        </span>
        <input
          value={templateKey}
          maxLength={80}
          onChange={(e) => setTemplateKey(e.target.value)}
          placeholder="e.g. department.internal-medicine"
        />
      </label>
      {problem && <p className="error">{problem}</p>}
      <div className="formactions">
        <button type="button" className="primary" disabled={busy || !name.trim() || !kind.trim()} onClick={add}>
          {busy ? 'Adding…' : `Add ${kind.trim() || 'unit'}`}
        </button>
      </div>
      <p className="legend muted">
        The kind is just a word — the list is a convenience, not a limit. If your organization has a level this platform
        has never met, type it and it exists.
      </p>
    </div>
  );
}

/** This unit itself: rename it, move it, see what it holds. */
function UnitCard({
  clientId,
  unit,
  units,
  onChanged,
  onGo,
}: {
  clientId: string;
  unit: OrgNode;
  units: OrgNode[];
  onChanged: () => void;
  onGo: (id: string) => void;
}) {
  const [summary, setSummary] = useState<OrgSummary | null>(null);
  const [name, setName] = useState(unit.name);
  const [templateKey, setTemplateKey] = useState(unit.templateKey ?? '');
  const [moveTo, setMoveTo] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    setName(unit.name);
    setTemplateKey(unit.templateKey ?? '');
    setMoveTo('');
    setProblem(null);
    setSummary(null);
    api.orgSummary(clientId, unit.id).then(setSummary).catch(() => setSummary(null));
  }, [clientId, unit.id]);

  const run = async (what: () => Promise<unknown>, done: string) => {
    setBusy(true);
    setProblem(null);
    try {
      await what();
      setToast(done);
      onChanged();
    } catch (e) {
      setProblem(e instanceof ApiError ? e.message : (e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // Somewhere this unit could legitimately go: not itself, and nothing inside
  // it — the API refuses both, and offering them would only invite the error.
  const inside = `${unit.path}${unit.id}/`;
  const destinations = units.filter((u) => u.id !== unit.id && u.id !== unit.parentId && !u.path.startsWith(inside));

  return (
    <div className="card logi">
      <h2>
        {unit.name} <span className="pill tiny">{kindLabel(unit.kind)}</span>
      </h2>
      {toast && <p className="toast noprint" onClick={() => setToast(null)}>{toast}</p>}
      {summary && (
        <ul className="orgstats">
          <li><strong>{summary.subtreeMembers}</strong> <span className="muted">{summary.subtreeMembers === 1 ? 'person' : 'people'} in all</span></li>
          <li><strong>{summary.directMembers}</strong> <span className="muted">directly here</span></li>
          <li><strong>{summary.descendantUnits}</strong> <span className="muted">unit{summary.descendantUnits === 1 ? '' : 's'} beneath</span></li>
          <li><strong>{summary.schedules}</strong> <span className="muted">schedule{summary.schedules === 1 ? '' : 's'}</span></li>
        </ul>
      )}
      {unit.templateKey && (
        <p className="muted small">
          Tagged <code>{unit.templateKey}</code> — the same kind of unit as its counterparts at other hospitals, and
          entirely separate from them.
        </p>
      )}

      {unit.parentId && (
        <>
          <label className="field">
            <span>Name</span>
            <input value={name} maxLength={120} onChange={(e) => setName(e.target.value)} />
          </label>
          <label className="field">
            <span>
              Same-kind tag <span className="muted">— optional; ties this to the matching unit at other hospitals</span>
            </span>
            <input
              value={templateKey}
              maxLength={80}
              placeholder="e.g. department.internal-medicine"
              onChange={(e) => setTemplateKey(e.target.value)}
            />
          </label>
          <div className="formactions">
            <button
              type="button"
              disabled={busy || !name.trim() || (name === unit.name && templateKey === (unit.templateKey ?? ''))}
              onClick={() =>
                run(
                  () =>
                    api.orgUpdateUnit(clientId, unit.id, {
                      name: name.trim(),
                      templateKey: templateKey.trim() || null,
                    }),
                  'Saved.',
                )
              }
            >
              {busy ? 'Saving…' : 'Save'}
            </button>
          </div>

          <label className="field">
            <span>Move into <span className="muted">— brings everything inside it along</span></span>
            <select value={moveTo} onChange={(e) => setMoveTo(e.target.value)}>
              <option value="">Leave where it is</option>
              {destinations.map((d) => (
                <option key={d.id} value={d.id}>{'— '.repeat(d.depth)}{d.name}</option>
              ))}
            </select>
          </label>
          <div className="formactions">
            <button
              type="button"
              disabled={busy || !moveTo}
              onClick={() => run(() => api.orgMoveUnit(clientId, unit.id, moveTo).then(() => onGo(unit.id)), 'Moved.')}
            >
              {busy ? 'Moving…' : 'Move'}
            </button>
          </div>
        </>
      )}
      {problem && <p className="error">{problem}</p>}
    </div>
  );
}

/** The schedules this unit holds — as many as it needs, each naming its own engine. */
function Schedules({ clientId, unit, units }: { clientId: string; unit: OrgNode; units: OrgNode[] }) {
  const [rows, setRows] = useState<OrgSchedule[] | null>(null);
  const [name, setName] = useState('');
  const [engineKey, setEngineKey] = useState('hospitalist-shift');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const load = () => {
    setRows(null);
    api.orgSchedules(clientId, unit.id).then(setRows).catch(() => setRows([]));
  };
  useEffect(() => { load(); setName(''); setProblem(null); }, [clientId, unit.id]);

  const add = async () => {
    setBusy(true);
    setProblem(null);
    try {
      await api.orgCreateSchedule(clientId, { orgUnitId: unit.id, name: name.trim(), engineKey });
      setName('');
      load();
    } catch (e) {
      setProblem(e instanceof ApiError ? e.message : (e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card logi">
      <h2>Schedules</h2>
      {!rows ? (
        <p className="muted">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="muted">No schedules on {unit.name} yet.</p>
      ) : (
        <ul className="orgschedules">
          {rows.map((s) => (
            <ScheduleRow key={s.id} clientId={clientId} schedule={s} units={units} onChanged={load} />
          ))}
        </ul>
      )}
      <label className="field">
        <span>Add a schedule</span>
        <input value={name} maxLength={120} onChange={(e) => setName(e.target.value)} placeholder="e.g. Cardiology Call" />
      </label>
      <label className="field inline">
        <span className="muted">Run by</span>
        <select value={engineKey} onChange={(e) => setEngineKey(e.target.value)}>
          <option value="hospitalist-shift">{engineLabel('hospitalist-shift')}</option>
          <option value="im-residency">{engineLabel('im-residency')}</option>
        </select>
      </label>
      {problem && <p className="error">{problem}</p>}
      <div className="formactions">
        <button type="button" disabled={busy || !name.trim()} onClick={add}>{busy ? 'Adding…' : 'Add schedule'}</button>
      </div>
      <p className="legend muted">
        One unit can hold as many schedules as it needs, and they do not have to be the same kind. A residency block
        schedule and a shift rota can sit side by side because each names the engine that runs it.
      </p>
    </div>
  );
}

/**
 * One schedule, and the chance to correct it.
 *
 * Picking the wrong engine is an easy mistake and used to be permanent —
 * schedules could be created and never edited. It can be changed here while
 * the schedule is still empty. Once it holds a year of blocks or a roster the
 * option disappears, and the row says what is holding it, because a disabled
 * control with no reason is how somebody ends up deleting real work to get
 * around it.
 */
function ScheduleRow({
  clientId,
  schedule,
  units,
  onChanged,
}: {
  clientId: string;
  schedule: OrgSchedule;
  /** Everywhere in this client it could live instead. */
  units: OrgNode[];
  onChanged: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(schedule.name);
  const [engineKey, setEngineKey] = useState(schedule.engineKey);
  const [orgUnitId, setOrgUnitId] = useState(schedule.orgUnitId);
  const [contents, setContents] = useState<{ empty: boolean; held: Record<string, number> } | null>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    api.orgScheduleContents(clientId, schedule.id).then(setContents).catch(() => setContents(null));
  }, [open, clientId, schedule.id]);

  const save = async () => {
    setBusy(true);
    setProblem(null);
    try {
      await api.orgUpdateSchedule(clientId, schedule.id, { name: name.trim(), engineKey, orgUnitId });
      setOpen(false);
      onChanged();
    } catch (e) {
      setProblem(e instanceof ApiError ? e.message : (e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const held = contents && !contents.empty ? Object.entries(contents.held) : [];
  return (
    <li>
      <strong>{schedule.name}</strong>
      <span className="pill tiny">{engineLabel(schedule.engineKey)}</span>
      <button type="button" className="linklike small" style={{ marginLeft: 'auto' }} onClick={() => setOpen(!open)}>
        {open ? 'Cancel' : 'Edit'}
      </button>
      {open && (
        <div style={{ flexBasis: '100%', marginTop: 8 }}>
          <label className="field">
            <span>Name</span>
            <input value={name} maxLength={120} onChange={(e) => setName(e.target.value)} />
          </label>
          <label className="field inline">
            <span className="muted">Run by</span>
            <select value={engineKey} disabled={!contents?.empty} onChange={(e) => setEngineKey(e.target.value)}>
              <option value="hospitalist-shift">{engineLabel('hospitalist-shift')}</option>
              <option value="im-residency">{engineLabel('im-residency')}</option>
            </select>
          </label>
          {contents && !contents.empty && (
            <p className="legend muted">
              The engine is fixed now: this schedule already holds{' '}
              {held.map(([label, n]) => `${n} ${label}`).join(', ')}. Those rows were written for one engine and would
              mean nothing to another, so nothing here converts them. If the engine is wrong, add a new schedule with
              the right one and import into that.
            </p>
          )}
          {contents?.empty && (
            <p className="legend muted">Nothing has been imported into this schedule yet, so the engine can still be changed.</p>
          )}
          <label className="field inline">
            <span className="muted">Sits under</span>
            <select value={orgUnitId} onChange={(e) => setOrgUnitId(e.target.value)}>
              {units.map((u) => (
                <option key={u.id} value={u.id}>{u.name}</option>
              ))}
            </select>
          </label>
          <p className="legend muted">
            Moving it is safe whatever it holds — everything in a schedule belongs to the schedule, not to the unit
            above it, so it travels with it.
          </p>
          {problem && <p className="error">{problem}</p>}
          <div className="formactions">
            <button type="button" className="primary" disabled={busy || !name.trim()} onClick={save}>
              {busy ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      )}
    </li>
  );
}

/** Who sits here, and who could. */
function People({ clientId, unit, onChanged }: { clientId: string; unit: OrgNode; onChanged: () => void }) {
  const [rows, setRows] = useState<OrgMember[] | null>(null);
  const [deep, setDeep] = useState(false);
  const [placeable, setPlaceable] = useState<OrgPerson[]>([]);
  const [pick, setPick] = useState('');
  const [role, setRole] = useState('physician');
  const [busy, setBusy] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  const load = () => {
    setRows(null);
    api.orgMembers(clientId, unit.id, deep).then(setRows).catch(() => setRows([]));
    api.orgPlaceable(clientId, unit.id).then(setPlaceable).catch(() => setPlaceable([]));
  };
  useEffect(() => { load(); setPick(''); setProblem(null); }, [clientId, unit.id, deep]);

  const act = async (id: string, what: () => Promise<unknown>) => {
    setBusy(id);
    setProblem(null);
    try {
      await what();
      load();
      onChanged();
    } catch (e) {
      setProblem(e instanceof ApiError ? e.message : (e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="card logi">
      <h2>
        People
        <button type="button" className="linklike small" onClick={() => setDeep(!deep)}>
          {deep ? 'Just this unit' : 'Include everyone beneath'}
        </button>
      </h2>
      {problem && <p className="error">{problem}</p>}
      {!rows ? (
        <p className="muted">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="muted">
          Nobody is placed in {unit.name}{deep ? ' or anything beneath it' : ''} yet.
        </p>
      ) : (
        <ul className="people">
          {rows.map((m) => (
            <li key={m.id}>
              <span>
                <strong>{m.physician.lastName}, {m.physician.firstName}</strong>{' '}
                <span className="muted small">{m.role}</span>
                {m.isPrimary && <span className="pill tiny">sits here</span>}
                {deep && m.orgUnitId !== unit.id && <span className="muted small"> · {m.orgUnit.name}</span>}
              </span>
              <span className="noprint">
                {!m.isPrimary && (
                  <button type="button" disabled={busy === m.id} onClick={() => act(m.id, () => api.orgSetPrimaryMember(clientId, m.id))}>
                    Sits here
                  </button>
                )}
                <button type="button" className="danger" disabled={busy === m.id} onClick={() => act(m.id, () => api.orgRemoveMember(clientId, m.id))}>
                  Remove
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}

      <label className="field">
        <span>Place someone here</span>
        <select value={pick} onChange={(e) => setPick(e.target.value)} disabled={placeable.length === 0}>
          <option value="">{placeable.length === 0 ? 'Nobody left to place here' : 'Choose a person…'}</option>
          {placeable.map((p) => <option key={p.id} value={p.id}>{p.lastName}, {p.firstName}</option>)}
        </select>
      </label>
      <label className="field inline">
        <span className="muted">As</span>
        <input list="org-roles" value={role} maxLength={40} onChange={(e) => setRole(e.target.value)} />
        <datalist id="org-roles">
          {ROLES.map((r) => <option key={r} value={r} />)}
        </datalist>
      </label>
      <div className="formactions">
        <button
          type="button"
          disabled={!!busy || !pick || !role.trim()}
          onClick={() => act('add', () => api.orgAddMember(clientId, { physicianId: pick, orgUnitId: unit.id, role: role.trim() }).then(() => setPick('')))}
        >
          {busy === 'add' ? 'Placing…' : 'Place'}
        </button>
      </div>
      <p className="legend muted">
        "Sits here" marks somebody's home unit — the one place that answers where they belong. Being credentialed to
        work somewhere is a separate record and does not change it.
      </p>
    </div>
  );
}

/** The kinds the list offers. Typing anything else is allowed and is the point. */
const KINDS = ['hospital', 'department', 'division', 'group', 'section', 'program'];

/** Titles the roles box offers, for the same reason and with the same freedom. */
const ROLES = ['physician', 'resident', 'attending', 'chief', 'coordinator', 'fellow'];

/** What you are most likely adding inside a unit of this kind. A suggestion, never a rule. */
function nextKind(parentKind: string): string {
  const order: Record<string, string> = {
    client: 'hospital',
    hospital: 'department',
    department: 'division',
    division: 'group',
    group: 'section',
  };
  return order[parentKind] ?? 'section';
}

const nextKindLabel = (parentKind: string) => kindLabel(nextKind(parentKind));

/** Every node of the tree, flattened — for the move picker and for finding the node in the URL. */
function flatten(node: OrgNode): OrgNode[] {
  return [node, ...node.children.flatMap(flatten)];
}

/** The path from the root down to one node, which is the trail across the top of the page. */
function breadcrumb(root: OrgNode, id: string): OrgNode[] {
  if (root.id === id) return [root];
  for (const child of root.children) {
    const below = breadcrumb(child, id);
    if (below.length) return [root, ...below];
  }
  return [];
}
